import type { MouseEvent } from 'react';

import { GLOBAL } from 'saddlebag-browser';

import BpkBadge from '@skyscanner/backpack-web/bpk-component-badge';
import BpkCard from '@skyscanner/backpack-web/bpk-component-card';
import TrendUpIcon from '@skyscanner/backpack-web/bpk-component-icon/sm/trend';
import TrendDownIcon from '@skyscanner/backpack-web/bpk-component-icon/sm/trend--down';
import {
  BpkBackgroundImage,
  withLazyLoading,
  withLoadingBehavior,
  BORDER_RADIUS_STYLES,
} from '@skyscanner/backpack-web/bpk-component-image';
import BpkText, {
  TEXT_STYLES,
  TEXT_COLORS,
} from '@skyscanner/backpack-web/bpk-component-text/src/BpkText';

import { logMiniEvent } from '../../../logMiniEvent';
import { useMBDContext } from '../../common/MBD/useMBDContext';
import getDealsCommonTrackingProps from '../getDealsCommonTrackingProps';

import DealARIADescriptor from './DealARIADescriptor';
import Flight from './Flight';
import PriceButton from './PriceButton/PriceButton';

import type {
  DealCardData,
  LastUpdated,
  LegWithFormattedDate,
  PageTypeOptions,
  PriceBandStrings,
  TrackingContext,
} from '@skyscanner-internal/landing-pages-shared-types/types-shared/DealsEvolved';
import type { StringTranslations } from '@skyscanner-internal/landing-pages-shared-types/types-shared/components';

import STYLES from './DealCard.module.scss';

type DealCardProps = {
  strings: StringTranslations;
  trackingContext: TrackingContext;
  pageTypeOptions: PageTypeOptions;
  priceBandStrings?: PriceBandStrings;
} & DealCardData;

const documentIfExists =
  typeof globalThis.window !== 'undefined' ? document : null;
const FadingLazyLoadedImage = withLoadingBehavior(
  withLazyLoading(BpkBackgroundImage, documentIfExists),
);

const onCardClicked = (props: {
  trackingContext: TrackingContext;
  pageTypeOptions: PageTypeOptions;
  deepLink: string;
}) => {
  logMiniEvent('DealsEvolvedEvent', 'deals_evolved_component', {
    deals_common: getDealsCommonTrackingProps(props),
    event_type: 'DEAL_CLICKED',
  });

  const globalWindow = GLOBAL.getWindow();
  globalWindow.setTimeout(() => {
    globalWindow.location.href = props.deepLink;
  }, 300);
};

const getLastUpdatedLegend = ({ highlight, text }: LastUpdated) => {
  if (highlight) {
    return <BpkBadge>{text}</BpkBadge>;
  }

  return (
    <BpkText textStyle={TEXT_STYLES.sm} color={TEXT_COLORS.textSecondary}>
      <span className={STYLES.DealCard__lastUpdatedLabel}>{text}</span>
    </BpkText>
  );
};

const getPriceBandLabel = (
  indicator: string,
  priceBandStrings?: PriceBandStrings,
): string => {
  if (!priceBandStrings) return '';

  switch (indicator) {
    case 'l':
      return priceBandStrings.low;
    case 't':
      return priceBandStrings.typical;
    case 'h':
      return priceBandStrings.high;
    default:
      return '';
  }
};

const getPriceBandBadgeClassName = (indicator: string): string => {
  switch (indicator) {
    case 'l':
      return STYLES.DealCard__priceBadgeLow;
    case 'h':
      return STYLES.DealCard__priceBadgeHigh;
    case 't':
    default:
      return STYLES.DealCard__priceBadgeTypical;
  }
};

const getPriceBandIcon = (indicator: string) => {
  if (indicator === 'l') {
    return (
      <TrendDownIcon
        className={`${STYLES.DealCard__badgeIcon} ${STYLES['DealCard__badgeIcon--low']}`}
        aria-hidden="true"
      />
    );
  }
  if (indicator === 'h') {
    return (
      <TrendUpIcon
        className={`${STYLES.DealCard__badgeIcon} ${STYLES['DealCard__badgeIcon--high']}`}
        aria-hidden="true"
      />
    );
  }
  return null;
};

const DealCard = ({
  cardIndex,
  dealCardTitle,
  deepLink,
  destination,
  direct,
  lastUpdated,
  legs,
  numberOfDealsRequested,
  numberOfDealsReturned,
  pageTypeOptions,
  price,
  priceBandIndicator,
  priceBandStrings = undefined,
  strings,
  trackingContext,
}: DealCardProps) => {
  const { buildMBDParam, rawComponentName } = useMBDContext();
  const mbdComponentName = `${rawComponentName}_card`;
  const { countryName, image, name: destinationName } = destination;
  const { url } = image || {};

  return (
    <BpkCard
      padded={false}
      href={deepLink}
      rel="nofollow"
      onClick={(e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
        onCardClicked({ trackingContext, pageTypeOptions, deepLink });
      }}
      data-testid={`dealCard_${cardIndex}`}
      data-tracking-element-id={`${mbdComponentName}_${cardIndex}`}
      data-tracking-common-params={buildMBDParam(
        `${mbdComponentName}_${cardIndex}`,
        {
          number_of_deals_requested: numberOfDealsRequested
            ? numberOfDealsRequested.toString()
            : 'null',
          number_of_deals_returned: numberOfDealsReturned
            ? numberOfDealsReturned.toString()
            : 'null',
          ...(priceBandStrings
            ? { price_band_label: priceBandIndicator || 'none' }
            : {}),
        },
      )}
    >
      <DealARIADescriptor
        {...{ strings, legs, direct, price, city: destinationName }}
      />

      <div aria-hidden="true" className={STYLES.DealCard__Image}>
        {url && (
          <FadingLazyLoadedImage
            altText={destinationName}
            aspectRatio={315 / 128}
            src={url}
            borderRadiusStyle={BORDER_RADIUS_STYLES.sm}
            imageStyle={{
              width: '100%',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              backgroundSize: 'cover',
              overflow: 'hidden',
              backgroundImage: `url(${url})`,
              borderRadius: '.375rem .375rem 0 0',
            }}
          />
        )}
        <div
          className={
            url
              ? STYLES.DealCard__mainBlockWithImage
              : STYLES.DealCard__mainBlock
          }
        >
          {priceBandIndicator && priceBandStrings && (
            <div className={STYLES.DealCard__priceBandBadgeContainer}>
              <BpkBadge
                type="normal"
                className={getPriceBandBadgeClassName(priceBandIndicator)}
                data-testid="price-band-indicator"
              >
                {getPriceBandIcon(priceBandIndicator)}
                {getPriceBandLabel(priceBandIndicator, priceBandStrings)}
              </BpkBadge>
            </div>
          )}
          {dealCardTitle && (
            <BpkText
              textStyle={TEXT_STYLES.heading4}
              className={
                countryName
                  ? STYLES.DealCard__destinationWithCountryName
                  : STYLES.DealCard__destination
              }
              data-testid="deal-from-to-city-names"
            >
              {dealCardTitle}
            </BpkText>
          )}
          {countryName && (
            <BpkText
              color={TEXT_COLORS.textSecondary}
              textStyle={TEXT_STYLES.sm}
              data-testid="deal-country-name"
              className={STYLES.DealCard__countryName}
            >
              {countryName}
            </BpkText>
          )}
          {(legs || []).map((leg: LegWithFormattedDate) => (
            <Flight
              key={JSON.stringify(leg)}
              leg={leg}
              strings={strings}
              direct={direct}
            />
          ))}
          <div className={STYLES.DealCard__footer}>
            {lastUpdated && getLastUpdatedLegend(lastUpdated)}
            <PriceButton strings={strings} price={price} />
          </div>
        </div>
      </div>
    </BpkCard>
  );
};

export default DealCard;
